/**
 * deviceValidation.js -- Form-data validation for device mutations
 * (validateFormData throws on invalid input), and the SOC rules the
 * Add/Edit Device form shows as field errors (socErrors).
 */

const APPROVED_FORM_FIELDS = [
  "device_name", "make_and_model", "serial_number", "device_type",
  "storage_mode", "production_type", "demand_charge", "status",
  "start_date", "end_date", "max_power_charging", "max_power_discharging",
  "min_power_charging", "min_power_discharging", "startup_cost",
  "fuel_type", "fuel_storage_size", "consumption", "carbon_emissions",
  "fuel_cost_per_kg", "fuel_cost_escalator_fraction", "historical_load_csv",
  "utility_tariff_csv", "building_load", "max_energy", "min_soc",
  "max_soc", "initial_soc", "avg_parasitic_load", "fractional_self_loss",
  "daily_cycles", "charging_efficiency_fraction",
  "discharging_efficiency_fraction",
  "average_energy_used_during_operation", "csv_file_name",
  "yearly_degradation", "charging_strategy", "discharging_strategy",
  "uploadedFile", "demand_charge_escalator_fraction",
  // The picker's answer: the device_ids this device is coupled to. Written
  // to both sides of every link by deviceMutations; absent means "leave the
  // couplings as they are".
  "coupled_device_ids",
];

/**
 * Validates that formValues contains only recognised fields.
 * @param {Object} formValues
 * @throws {Error} if formValues is falsy or contains unrecognised keys
 */
export function validateFormData(formValues) {
  if (!formValues) {
    throw new Error("Form values are required");
  }
  for (const key in formValues) {
    if (!APPROVED_FORM_FIELDS.includes(key)) {
      throw new Error(`Invalid form field: ${key}`);
    }
  }
}

const isBlank = (v) => v == null || v === "NA" || String(v).trim() === "";

// A SOC value must be a plain decimal ("20", "33.5"), the pattern the form's
// other numeric fields accept. For those, Number here and the save's
// parseFloat (units.js) read the same value, so what passes is what saves;
// "0x14" or "1e2" would pass Number but save as 0 or 1. A leading minus is
// read only so that "-5" is reported as out of range, not as not a number.
const DECIMAL = /^-?\d+(\.\d+)?$/;
const isNumber = (v) => !isBlank(v) && DECIMAL.test(String(v).trim());
const toNumber = (v) => Number(String(v).trim());

/**
 * The form errors for a storage device's SOC fields, in percent (0-100).
 * Used for every BESS, and for an EV whose SOC wasn't read from its
 * availability file (the user typed it in, or is editing a saved EV).
 *
 * Each value is required and must be a plain decimal. Min is 0-100, max is
 * 1-100, and initial is 0-100 and not below min. The rest depends on the
 * storage mode:
 *
 * - BESS: min must be below max. Initial above max is allowed, so batteries
 *   saved that way keep saving, though the sim's MILP caps a battery's initial
 *   SOC at its max (Grene.DT.sim, der_twin/der_milp/der_milp_variables.py).
 * - EV: min <= initial <= max. Min may equal max: the sim refuses only min
 *   above max (devices/bidirectional_device.py), and the admin derives
 *   min == max from a file whose SOC above zero is constant. Initial above max
 *   is refused because the MILP would cap it at max, so the EV wouldn't start
 *   where the user said.
 *
 * @param {{min_soc: *, max_soc: *, initial_soc: *}} values - form values.
 * @param {{storageMode?: string}} [options] - storageMode "EV" applies the EV
 *   rules; anything else applies the BESS rules.
 * @returns {Object<string, string>} Messages keyed by field id; {} when valid.
 */
export function socErrors({ min_soc, max_soc, initial_soc }, { storageMode } = {}) {
  const ev = storageMode === "EV";
  const errors = {};

  if (isBlank(initial_soc)) {
    errors.initial_soc = "Initial SOC is required";
  } else if (!isNumber(initial_soc)) {
    errors.initial_soc = "Initial SOC must be a number";
  } else if (isNumber(min_soc) && toNumber(initial_soc) < toNumber(min_soc)) {
    errors.initial_soc = "Initial SOC must be greater than or equal to Minimum SOC";
  } else if (ev && isNumber(max_soc) && toNumber(initial_soc) > toNumber(max_soc)) {
    errors.initial_soc = "Initial SOC must be less than or equal to Maximum SOC";
  } else if (toNumber(initial_soc) < 0 || toNumber(initial_soc) > 100) {
    errors.initial_soc = "Initial SOC must be between 0 and 100";
  }

  if (isBlank(min_soc)) {
    errors.min_soc = "Minimum SOC is required";
  } else if (!isNumber(min_soc)) {
    errors.min_soc = "Minimum SOC must be a number";
  } else if (toNumber(min_soc) < 0 || toNumber(min_soc) > 100) {
    errors.min_soc = "Minimum SOC must be between 0 and 100";
  }

  if (isBlank(max_soc)) {
    errors.max_soc = "Maximum SOC is required";
  } else if (!isNumber(max_soc)) {
    errors.max_soc = "Maximum SOC must be a number";
  } else if (toNumber(max_soc) < 1 || toNumber(max_soc) > 100) {
    errors.max_soc = "Maximum SOC must be between 1 and 100";
  }

  if (isNumber(min_soc) && isNumber(max_soc)) {
    const min = toNumber(min_soc);
    const max = toNumber(max_soc);
    if (ev && min > max) {
      errors.min_soc = "Min SOC must be less than or equal to Max SOC";
      errors.max_soc = "Max SOC must be greater than or equal to Min SOC";
    } else if (!ev && min >= max) {
      errors.min_soc = "Min SOC must be less than Max SOC";
      errors.max_soc = "Max SOC must be greater than Min SOC";
    }
  }

  return errors;
}
